"use client";
import React, { useState } from "react";
import { Form, Formik } from "formik";
import { Button } from "@/app/(components)/Basic/Button";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import { NftDetailsListItem } from "../NftDetailsListItem";
import { CurrencyInput } from "@/app/(components)/Basic/CurrencyInput";
import FormDropdownWithDatePicker from "@/app/(components)/Basic/FormDropdownWithDatePicker";
import { OfferOrder } from "@/api/orders/types";
import useAccountBalance from "@/shared/hooks/useAccountBalance";
import usePaymentCurrency from "@/shared/hooks/usePaymentCurrency";
import { useMutation } from "@tanstack/react-query";
import ordersApi from "@/api/orders/client";
import { useWeb3State } from "@/web3/context";
import { addTakerFee, toLowDenom } from "@/shared/currencies";
import { toSeconds } from "@/shared/dates";
import { Type } from "@/app/(components)/Basic/Button/constants";
import useSignedFunction from "@/shared/hooks/useSignedFunction";
import useTotalTakerPrice from "@/shared/hooks/useTotalTakerPrice";
import useExchangeERC20ApprovalMutation from "@/web3/contracts/common/actions/useExchangeERC20ApprovalMutation";
import useEIP712Signature from "@/eip712/hooks/useEIP712Signature";
import { getSignedOfferMessage } from "@/eip712";
import { Collection } from "@/api/collections/types";
import { CollectionModalBase } from "../CollectionModalBase";
import * as Yup from "yup";
import usePaymentMethods from "@/app/(components)/Basic/CurrencyInput/hooks/usePaymentMethods";
import useOrdersInvalidation from "@/shared/hooks/useOrdersInvalidation";
import { OFFERS_DROPDOWN_DATE_PICKER_OPTIONS } from "@/shared/constants";
import useCollectionExchangeFees from "@/web3/contracts/PaymentManager/actions/useCollectionExchangeFees";

export interface Props {
  collection: Collection;
  onSuccess: () => void;
  onClose: () => void;
}

function NftCollectionOfferModal({ collection, onSuccess, onClose }: Props) {
  const [isOpen, setIsOpen] = useState(true);
  const [offerId, setOfferId] = useState("");

  const [price, setPrice] = useState<string>("");
  const currencies = usePaymentMethods(true);
  const [paymentErc20TokenAddress, setPaymentMethodAddress] = useState(
    currencies && currencies[0].value,
  );

  const collectionFeeData = useCollectionExchangeFees(collection.address);
  const { currentAddress } = useWeb3State();

  const paymentMethod = usePaymentCurrency(paymentErc20TokenAddress);

  const { balanceInCrypto } = useAccountBalance(paymentErc20TokenAddress);

  const { approve, isLoading: isApproveLoading } =
    useExchangeERC20ApprovalMutation(paymentErc20TokenAddress);

  const { totalPrice, totalPriceInUSD } = useTotalTakerPrice({
    price,
    paymentErc20TokenAddress,
    takerFee: collectionFeeData?.rawTakerFee,
  });

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  const invalidateOrders = useOrdersInvalidation();

  const sendMessageForSign = useEIP712Signature({
    onError: async () => {
      await ordersApi.deleteBid(offerId);
      invalidateOrders();
    },
  });

  const { mutateAsync: requestSignature, isLoading: isSignatureLoading } =
    useMutation({
      mutationFn: async (offer: OfferOrder) => {
        const offerTypedData = getSignedOfferMessage(offer);

        const signature = await sendMessageForSign(offerTypedData);

        await ordersApi.updateOfferSignature(offer.id!, signature);

        setOfferId(offer.id!);
      },
      onSuccess: () => {
        invalidateOrders();
        onSuccess();
      },
    });

  const { mutateAsync: createOffer, isLoading: isCreateLoading } = useMutation({
    mutationFn: (offer: OfferOrder) => {
      setOfferId(offer.orderNonce!);

      return ordersApi.createOffer(offer);
    },
    onSuccess: (offer: OfferOrder) => {
      requestSignature(offer);
    },
  });

  const checkedCreateOffer = useSignedFunction(createOffer);

  const handleSubmit = async ({
    expiresAt,
    price,
    paymentErc20TokenAddress,
  }: any) => {
    const expiresAtDate = new Date(expiresAt);

    const rawPrice = toLowDenom(price, paymentMethod?.decimals);

    const totalPrice = addTakerFee(rawPrice!, collectionFeeData?.rawTakerFee);

    const isApproved = await approve(totalPrice!);

    if (!isApproved) return;

    await checkedCreateOffer({
      nftContract: collection.address,
      tokenId: 0,
      buyer: currentAddress!,
      price: toLowDenom(price),
      totalPrice: totalPrice?.toString(),
      paymentErc20TokenAddress,
      expiresAt: toSeconds(expiresAtDate),
      isForCollection: true,
      makerFee: collectionFeeData?.rawMakerFee,
      takerFee: collectionFeeData?.rawTakerFee,
      royaltiesFee: collectionFeeData?.rawRoyaltiesFee,
      royaltiesRecipient: collectionFeeData?.royaltiesRecipient,
    });
  };

  const updatePrice = (value: any) => {
    if (typeof value === "object") return;

    const rawPrice = toLowDenom(value, paymentMethod?.decimals);

    setPrice(rawPrice!);
  };

  const isLoading = isCreateLoading || isApproveLoading || isSignatureLoading;

  const validationSchema = Yup.object().shape({
    price: Yup.number()
      .required("Price is required")
      .test(
        "maxDecimals",
        "Price can have a maximum of 6 decimals",
        (value) =>
          value === undefined || /^\d+(\.\d{1,6})?$/.test(value.toString()),
      )
      .min(0.0001, `Min offer is 0.0001 ${paymentMethod?.label}`)
      .max(+balanceInCrypto, `You don't have enough ${paymentMethod?.label}`),
    expiresAt: Yup.string()
      .required("Expire date is required")
      .test("isValidDate", "Date must be after current date", (value) => {
        const startingDate = new Date();
        const expiresDate = new Date(value);
        return expiresDate > startingDate;
      }),
  });

  return (
    <CollectionModalBase
      isOpen={isOpen}
      onClose={handleClose}
      title="Make collection offer"
      collection={collection}
      disableClose={isLoading}
    >
      <Formik
        initialValues={{
          price: undefined,
          expiresAt: "",
          paymentErc20TokenAddress: currencies && currencies[0].value,
          expiresAtDropdown: OFFERS_DROPDOWN_DATE_PICKER_OPTIONS[0].value,
        }}
        validateOnChange={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <div className="w-full">
            <CurrencyInput
              name="price"
              label="Your offer"
              shortInfo={`Balance: ${balanceInCrypto} ${paymentMethod?.label}`}
              onChange={updatePrice}
              onPaymentMethodChange={setPaymentMethodAddress}
              allThemesWithWhiteBackground
              onlyErc20Support
              disabled={isLoading}
            />
          </div>
          <FormDropdownWithDatePicker
            disabled={isLoading}
            options={OFFERS_DROPDOWN_DATE_PICKER_OPTIONS}
            dateName="expiresAt"
            dropdownName="expiresAtDropdown"
            label="Duration"
          />
          <NftDetailsListItem
            small
            label="Service fee"
            className="night:text-secondary"
            labelClassName="endemic:text-endemicGray-700"
          >
            {collectionFeeData?.takerFee}%
          </NftDetailsListItem>
          <NftDetailsListItem
            small
            label="Total amount"
            labelClassName="endemic:text-endemicGray-700"
          >
            <div className="flex flex-col sm:flex-row text-right sm:text-left items-center">
              {totalPrice?.rounded || "-"} {paymentMethod?.label}
              <span className="text-body-3 text-secondary endemic:text-disabled  ml-2">
                {totalPriceInUSD ? `${totalPriceInUSD} USD` : ""}
              </span>
            </div>
          </NftDetailsListItem>
          <div className="flex justify-center mt-10">
            <Button
              disabled={isLoading}
              className="mx-2"
              onClick={handleClose}
              endIcon={<ArrowRightIcon className="w-4" />}
            >
              Cancel
            </Button>
            <Button
              element={Type.Button}
              type="submit"
              disabled={isLoading}
              className="mx-2"
              endIcon={!isLoading && <ArrowRightIcon className="w-4" />}
            >
              {isLoading ? "Loading" : "Make offer"}
            </Button>
          </div>
        </Form>
      </Formik>
    </CollectionModalBase>
  );
}

export default NftCollectionOfferModal;
